"use client";

import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import type { FeedPost, FeedRequest, FeedResponse } from "@/actions/post";
import { Button } from "@/components/ui/button";
import { usePostFeed } from "@/hooks/use-posts";
import type { CommentsSheetViewer } from "./comments-sheet";
import { Post } from "./post";

interface PostListClientProps {
  context: FeedRequest;
  initialLimit: number;
  viewer?: CommentsSheetViewer;
  composer?: ReactNode;
  className?: string;
  emptyState?: ReactNode;
  initialPosts?: FeedPost[];
  initialFeed?: FeedResponse;
}

export function PostListClient({
  context,
  initialLimit,
  viewer,
  composer,
  className,
  emptyState,
  initialPosts,
  initialFeed,
}: PostListClientProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    error,
    fetchNextPage,
    fetchStatus,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = usePostFeed(context, initialLimit, initialPosts, { initialFeed });

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          void fetchNextPage();
        }
      });
    });

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Show error toast
  useEffect(() => {
    if (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Unable to load posts";
      toast.error(message);
    }
  }, [error]);

  // Flatten all posts from all pages
  const allPosts = data?.pages.flatMap((page) => page.posts) ?? [];

  if (isLoading) {
    return (
      <div className={className}>
        {composer}
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          {fetchStatus === "paused" ? (
            <>
              <p className="text-sm text-text-secondary">
                Posts are waiting for network access.
              </p>
              <Button variant="outline" onClick={() => void refetch()}>
                Retry
              </Button>
            </>
          ) : (
            <Loader2 className="h-6 w-6 animate-spin text-text-secondary" />
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        {composer}
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          <p className="text-sm text-text-secondary">Unable to load posts.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {composer}

      <div className="space-y-4">
        {allPosts.length === 0 && emptyState
          ? emptyState
          : allPosts.map((post) => (
              <Post key={post.id} post={post} viewer={viewer} />
            ))}

        <div ref={sentinelRef} />

        {hasNextPage && (
          <div className="flex justify-center py-4">
            {isFetchingNextPage ? (
              <Loader2 className="h-5 w-5 animate-spin text-text-secondary" />
            ) : (
              <Button variant="ghost" onClick={() => void fetchNextPage()}>
                Load more
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
